
import * as React from 'react';
import { Action } from 'redux';
import { connect } from 'react-redux';
import * as Creators from '../../actions/creators';
import { get } from 'lodash';
import { ICanaryMetricConfig, ICanaryState, DisableableTextarea, DISABLE_EDIT_CONFIG, FormRow, editingMetricSelector} from '@spinnaker/kayenta';
import { ICanaryMetricValidationErrors, MetricValidatorFunction } from '../../edit/editMetricValidation';
import { createSelector } from 'reselect';
import Ajv from 'ajv';
import { metricStatSchema } from './domain/MetricStatModel';
import { JsonEditor } from '@spinnaker/core';

interface ICloudwatchMetricConfigurerStateProps {
    editingMetric: ICanaryMetricConfig;
    validationErrors: ICanaryMetricValidationErrors;
}

interface ICloudwatchMetricConfigurerDispatchProps {
  changeInlineTemplate: (template: string) => void;
}

type ICloudwatchMetricConfigurerProps = ICloudwatchMetricConfigurerStateProps & ICloudwatchMetricConfigurerDispatchProps;

export const queryFinder = (metric: ICanaryMetricConfig) => get(metric, 'query.customInlineTemplate', '');
const ajv = new Ajv()
const validate = ajv.compile(metricStatSchema)

/*
 * Component for configuring a Cloudwatch metric
 */
function CloudwatchMetricConfigurer({ changeInlineTemplate, editingMetric, validationErrors }: ICloudwatchMetricConfigurerProps) {
    return (
          <FormRow label="Template" inputOnly={true} helpId="canary.config.filterTemplate" error={get(validationErrors, 'template.message', null)}>
            <section className="kayenta-config-json">
              <JsonEditor value={queryFinder(editingMetric)}  debounceChangePeriod={500} onChange={changeInlineTemplate} readOnly={false} />
            </section>
            <span className="body-small color-text-caption" style={{ marginTop: '5px' }}>
              Enter a valid <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/APIReference/API_MetricStat.html">MetricStat</a>
              JSON like these <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/APIReference/API_GetMetricData.html">examples</a>.
            </span>
          </FormRow>
    );
  }

/**
 * Validates Cloudwatch specific fields on the edit metric modal
 */
export function validateMetric(editingMetric: ICanaryMetricConfig): ICanaryMetricValidationErrors {
  const errors: ICanaryMetricValidationErrors = {};

  const validators: MetricValidatorFunction[] = [...getCloudwatchValidators(editingMetric)];

  return validators.reduce((reducedErrors, validator) => validator(reducedErrors, editingMetric), errors);
}

/**
 * Returns the list of validators for the Cloudwatch edit metric form.
 */
export function getCloudwatchValidators(editingMetric: ICanaryMetricConfig): MetricValidatorFunction[] {
  if (!editingMetric || !editingMetric.query) {
    return [];
  }
  return [validateTemplateMethod];
}

/**
 * Validates that the user has supplied a vaid JSON for the Inline template
 */
function validateTemplateMethod(
  errors: ICanaryMetricValidationErrors,
  editingMetric: ICanaryMetricConfig,
): ICanaryMetricValidationErrors {
  const nextErrors = { ...errors };
  const template = queryFinder(editingMetric);
  if (!template) {
    nextErrors.template = { message: 'The Cloudwatch template is required.' };
    return nextErrors;
  }
  if (!IsJsonString(template)) {
    nextErrors.template = { message: 'The Cloudwatch template must be a valid JSON.' };
    return nextErrors;
  }
  if (!validate(JSON.parse(template))) {
    nextErrors.template = { message: 'Invalid MetricStat JSON: ' + validate.errors[0].message };
    return nextErrors;
  }
  return nextErrors;
}

function IsJsonString(str: string) {
  try {
      JSON.parse(str);
  } catch (e) {
      return false;
  }
  return true;
}

const cloudwatchEditingMetricValidationErrorsSelector = createSelector(editingMetricSelector, validateMetric);

function mapStateToProps(state: ICanaryState): ICloudwatchMetricConfigurerStateProps {
  return {
    editingMetric: state.selectedConfig.editingMetric,
    validationErrors: cloudwatchEditingMetricValidationErrorsSelector(state),
  };
}

function mapDispatchToProps(dispatch: (action: Action & any) => void): ICloudwatchMetricConfigurerDispatchProps {
  return {
    changeInlineTemplate: (template: string) => {
      console.log(template);
      dispatch(Creators.updateCloudwatchInlineTemplate({ value: template }));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CloudwatchMetricConfigurer);
